import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  CircularProgress,
  Box,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { MediaSpecificUserUsageWithDuration } from '../../types';
import { useMst } from '../../model/Root';

interface MediaSpecificUserUsageDialogProps {
  open: boolean;
  onClose: () => void;
  data: MediaSpecificUserUsageWithDuration | null;
  loading: boolean;
}

const MediaSpecificUserUsageDialog: React.FC<MediaSpecificUserUsageDialogProps> = observer(
  ({ open, onClose, data, loading }) => {
    const { t } = useTranslation();
    const { errorStore } = useMst();
    const [timeUnit, setTimeUnit] = React.useState<'minutes' | 'hours'>('minutes');

    // Clear errors when dialog opens
    React.useEffect(() => {
      if (open) {
        errorStore.clearError();
      }
    }, [open, errorStore]);

    const formatTimeByUnit = (seconds: number | null): string => {
      if (!seconds) return timeUnit === 'minutes' ? '0 min' : '0.0h';

      if (timeUnit === 'minutes') {
        return `${Math.round(seconds / 60)} min`;
      } else {
        return `${(seconds / 3600).toFixed(1)}h`;
      }
    };

    const formatDuration = (seconds: number | null): string => {
      if (!seconds) return '0m';
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }
      return `${minutes}m`;
    };

    const formatMinutes = (seconds: number | null): number => {
      if (!seconds) return 0;
      return Math.round(seconds / 60);
    };

    const hasApiError =
      errorStore.error?.message &&
      (errorStore.error.message.includes('Request failed') ||
        errorStore.error.message.includes('500') ||
        errorStore.error.message.includes('Network Error'));

    const handleClose = () => {
      errorStore.clearError();
      onClose();
    };

    return (
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          {data && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                <Box>
                  <Typography variant="h6">
                    {t('reports.media_specific_usage_title')}: {data.mediaName || 'Unknown Media'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('reports.media_type')}: {data.mediaType || 'Unknown Type'}
                    {data.isTimeTracked && ` (${t('reports.tracked')})`}
                  </Typography>
                  {data.totalDurationSeconds !== null && data.totalDurationSeconds !== undefined && (
                    <Typography variant="body2" color="text.secondary">
                      {t('reports.total_duration')}: {formatDuration(data.totalDurationSeconds)}
                    </Typography>
                  )}
                </Box>
                <ToggleButtonGroup
                  value={timeUnit}
                  exclusive
                  onChange={(event, newTimeUnit) => {
                    if (newTimeUnit !== null) {
                      setTimeUnit(newTimeUnit);
                    }
                  }}
                  size="small"
                >
                  <ToggleButton value="minutes">{t('reports.minutes')}</ToggleButton>
                  <ToggleButton value="hours">{t('reports.hours')}</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {loading && (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          )}
          {!loading && hasApiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2">{t('reports.api_not_ready_message')}</Typography>
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                Error: {errorStore.error?.message}
              </Typography>
            </Alert>
          )}
          {!loading && data && !hasApiError && (
            <>
              {(data.totalDurationSeconds === null || data.totalDurationSeconds === undefined) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">{t('reports.missing_duration_message')}</Typography>
                </Alert>
              )}
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 150 }}>{t('reports.user_name')}</TableCell>
                    <TableCell sx={{ minWidth: 200 }}>{t('reports.user_email')}</TableCell>
                    {data.totalDurationSeconds !== null && data.totalDurationSeconds !== undefined ? (
                      <>
                        <TableCell align="right" sx={{ minWidth: 160 }}>
                          {t('reports.listened_vs_total_duration')}
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 120 }}>
                          {timeUnit === 'minutes' ? t('reports.overflow_minutes') : t('reports.overflow_hours')}
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 120 }}>
                          {t('reports.total_listening_time')}
                        </TableCell>
                      </>
                    ) : (
                      <TableCell align="right" sx={{ minWidth: 120 }}>
                        {t('reports.listened_duration')}
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.userUsages && data.userUsages.length > 0 ? (
                    data.userUsages.map((usage, index) => {
                      const listenedSeconds = usage.totalSecondsListened || 0;
                      const totalSeconds = data.totalDurationSeconds || 0;
                      const listenedMinutes = formatMinutes(listenedSeconds);
                      const totalMinutes = formatMinutes(totalSeconds);

                      // For completion status - cap listened time at total duration
                      const cappedListenedSeconds =
                        totalSeconds > 0 ? Math.min(listenedSeconds, totalSeconds) : listenedSeconds;

                      const overflowMinutes =
                        data.totalDurationSeconds !== null && data.totalDurationSeconds !== undefined
                          ? Math.max(0, listenedMinutes - totalMinutes)
                          : 0;
                      const overflowSeconds = overflowMinutes * 60;
                      const isCompleted =
                        data.totalDurationSeconds !== null &&
                        data.totalDurationSeconds !== undefined &&
                        totalSeconds > 0 &&
                        listenedSeconds >= totalSeconds;

                      return (
                        <TableRow key={index}>
                          <TableCell>{usage.userName || 'Unknown User'}</TableCell>
                          <TableCell>{usage.userEmail || 'No Email'}</TableCell>
                          {data.totalDurationSeconds !== null && data.totalDurationSeconds !== undefined ? (
                            <>
                              <TableCell
                                align="right"
                                sx={{
                                  color: isCompleted ? 'success.main' : 'inherit',
                                  fontWeight: isCompleted ? 'bold' : 'normal',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {`${formatTimeByUnit(cappedListenedSeconds)} / ${formatTimeByUnit(totalSeconds)}`}
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                {overflowSeconds > 0
                                  ? formatTimeByUnit(overflowSeconds)
                                  : timeUnit === 'minutes'
                                    ? '0 min'
                                    : '0.0h'}
                              </TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                {formatTimeByUnit(listenedSeconds)}
                              </TableCell>
                            </>
                          ) : (
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              {formatTimeByUnit(listenedSeconds)}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={data.totalDurationSeconds !== null && data.totalDurationSeconds !== undefined ? 5 : 3}
                        align="center"
                      >
                        <Typography variant="body2" color="text.secondary">
                          {t('reports.no_user_usage_data')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {/* Total Row */}
                  {data.userUsages && data.userUsages.length > 0 && (
                    <TableRow sx={{ borderTop: 2, borderColor: 'divider' }}>
                      <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>
                        {t('reports.total')}
                      </TableCell>
                      {data.totalDurationSeconds !== null && data.totalDurationSeconds !== undefined ? (
                        <>
                          <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            {/* Total completion (sum of capped times) */}
                            {formatTimeByUnit(
                              data.userUsages.reduce((sum, usage) => {
                                const listenedSeconds = usage.totalSecondsListened || 0;
                                const totalSeconds = data.totalDurationSeconds || 0;
                                const cappedSeconds =
                                  totalSeconds > 0 ? Math.min(listenedSeconds, totalSeconds) : listenedSeconds;
                                return sum + cappedSeconds;
                              }, 0)
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            {/* Total overflow */}
                            {formatTimeByUnit(
                              data.userUsages.reduce((sum, usage) => {
                                const listenedSeconds = usage.totalSecondsListened || 0;
                                const totalSeconds = data.totalDurationSeconds || 0;
                                const overflowSeconds = Math.max(0, listenedSeconds - totalSeconds);
                                return sum + overflowSeconds;
                              }, 0)
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            {/* Total listening time */}
                            {formatTimeByUnit(
                              data.userUsages.reduce((sum, usage) => sum + (usage.totalSecondsListened || 0), 0)
                            )}
                          </TableCell>
                        </>
                      ) : (
                        <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                          {formatTimeByUnit(
                            data.userUsages.reduce((sum, usage) => sum + (usage.totalSecondsListened || 0), 0)
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    );
  }
);

export default MediaSpecificUserUsageDialog;
